export type RelationshipPhenomenon =
  | 'pollination'
  | 'seed-dispersal'
  | 'habitat-relationship'
  | 'plant-animal-dependency';

export interface RelationshipOrganism {
  readonly id: string;
  readonly displayName: string;
  readonly role: 'plant' | 'animal' | 'habitat-part';
}

export interface RelationshipCondition {
  readonly id: string;
  readonly label: string;
  readonly observationFocus: string;
}

export interface RelationshipInteraction {
  readonly id: string;
  readonly conditionId: string;
  readonly actorOrganismIds: readonly string[];
  readonly observation: string;
}

export interface RelationshipEvidenceTarget {
  readonly id: string;
  readonly label: string;
  readonly sourceInteractionIds: readonly string[];
}

export interface RelationshipInvestigationHints {
  readonly observe: readonly string[];
  readonly predict: readonly string[];
  readonly investigate: readonly string[];
  readonly collectEvidence: readonly string[];
  readonly compare: readonly string[];
  readonly apply: readonly string[];
}

export interface RelationshipInvestigationConfig {
  readonly id: string;
  readonly masteryKey: string;
  readonly phenomenon: RelationshipPhenomenon;
  readonly title: string;
  readonly sceneObservation: string;
  readonly predictionPrompt: string;
  readonly organisms: readonly RelationshipOrganism[];
  readonly conditions: readonly RelationshipCondition[];
  readonly interactions: readonly RelationshipInteraction[];
  readonly requiredInteractionIds: readonly string[];
  readonly evidenceTargets: readonly RelationshipEvidenceTarget[];
  readonly requiredEvidenceIds: readonly string[];
  readonly comparisonPrompt: string;
  readonly supportedConditionId: string;
  readonly unsupportedComparisonFeedback: string;
  readonly evidenceStatement: string;
  readonly applicationPrompt: string;
  readonly conclusionId: string;
  readonly completionWorldEvent: string;
  readonly hints: RelationshipInvestigationHints;
}

function findDuplicates(values: readonly string[]) {
  return values.filter((value, index) => values.indexOf(value) !== index);
}

/** Reject malformed content before a presentation binds it to production art. */
export function validateRelationshipInvestigationConfig(
  config: RelationshipInvestigationConfig,
) {
  const organismIds = config.organisms.map(organism => organism.id);
  const conditionIds = config.conditions.map(condition => condition.id);
  const interactionIds = config.interactions.map(interaction => interaction.id);
  const evidenceIds = config.evidenceTargets.map(evidence => evidence.id);
  const duplicateIds = [
    ...findDuplicates(organismIds),
    ...findDuplicates(conditionIds),
    ...findDuplicates(interactionIds),
    ...findDuplicates(evidenceIds),
  ];

  if (!config.id || !config.masteryKey || duplicateIds.length > 0) {
    throw new Error(`Invalid relationship investigation identity: ${config.id}`);
  }
  if (!conditionIds.includes(config.supportedConditionId)) {
    throw new Error(`Unknown supported condition: ${config.supportedConditionId}`);
  }
  for (const interaction of config.interactions) {
    if (!conditionIds.includes(interaction.conditionId)) {
      throw new Error(`Unknown condition for interaction: ${interaction.id}`);
    }
    if (interaction.actorOrganismIds.some(id => !organismIds.includes(id))) {
      throw new Error(`Unknown organism for interaction: ${interaction.id}`);
    }
  }
  if (config.requiredInteractionIds.some(id => !interactionIds.includes(id))) {
    throw new Error(`Unknown required interaction in ${config.id}`);
  }
  for (const evidence of config.evidenceTargets) {
    if (evidence.sourceInteractionIds.some(id => !interactionIds.includes(id))) {
      throw new Error(`Unknown evidence source: ${evidence.id}`);
    }
  }
  if (config.requiredEvidenceIds.some(id => !evidenceIds.includes(id))) {
    throw new Error(`Unknown required evidence in ${config.id}`);
  }
  const requiredEvidence = config.evidenceTargets.filter(evidence =>
    config.requiredEvidenceIds.includes(evidence.id));
  if (requiredEvidence.some(evidence =>
    evidence.sourceInteractionIds.some(id => !config.requiredInteractionIds.includes(id)))) {
    throw new Error(`Required evidence has an optional-only source in ${config.id}`);
  }
  return config;
}
